import { observer } from "mobx-react-lite";
import type { ReactNode } from "react";
import styled from "styled-components";

import { useStores } from "../stores/useStores";
import { color } from "../theme/color";
import { Icon } from "./ui/Icon";
import type { IconName } from "./ui/Icon";

/** The songs, setlists and settings tabs: at the bottom on a phone, on the left on a tablet. */
export const Shell = observer(function Shell({ children }: { children: ReactNode }) {
    const { navigation } = useStores();
    const screen = navigation.screen.name;

    const tabs: { name: "songs" | "setlists" | "settings"; label: string; icon: IconName }[] = [
        { name: "songs", label: "Songs", icon: "songs" },
        { name: "setlists", label: "Setlists", icon: "setlist" },
        { name: "settings", label: "Settings", icon: "settings" },
    ];

    return (
        <Layout>
            <Nav aria-label="Main">
                {tabs.map((tab) => (
                    <Tab key={tab.name} aria-current={screen === tab.name && "page"} onClick={() => navigation.go({ name: tab.name })}>
                        <Icon name={tab.icon} />
                        {tab.label}
                    </Tab>
                ))}
            </Nav>
            <Content>{children}</Content>
        </Layout>
    );
});

const Layout = styled.div`
    height: 100%;
    display: flex;
    flex-direction: column-reverse;

    @media (min-width: 700px) {
        flex-direction: row;
    }
`;

const Nav = styled.nav`
    display: flex;
    border-top: 1px solid ${color.border};
    background: ${color.background};
    padding-bottom: env(safe-area-inset-bottom);

    @media (min-width: 700px) {
        flex-direction: column;
        gap: 8px;
        width: 84px;
        padding: 20px 8px 0;
        border-top: 0;
        border-right: 1px solid ${color.border};
        background: ${color.panel};
    }
`;

const Tab = styled.button`
    flex: 1;
    min-height: 56px;
    border: 0;
    border-radius: 12px;
    background: transparent;
    color: ${color.textMuted};
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 4px;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;

    &[aria-current="page"] {
        background: ${color.accentSoft};
        color: ${color.accent};
    }

    @media (max-width: 699px) {
        &[aria-current="page"] {
            background: transparent;
        }
    }

    @media (min-width: 700px) {
        flex: none;
        height: 64px;
    }
`;

const Content = styled.main`
    flex: 1;
    min-height: 0;
    min-width: 0;
    display: flex;
`;
